/**
 * The typed place in the browser (SA-1051 spec §3, §7.2): the twin of backend
 * `app.services.places`' readers, pinned by shared/place-cases.json. A legacy
 * string reads as the place it would normalize to (its code through
 * `stationCode`, its words as typed). Nothing here resolves: the server does,
 * at every write.
 * SA-1052 adds the customer's alternatives: a fifth state, the optional `alternatives` key, and two pure editing helpers for the picker.
 */
import type { Airport } from '@/types/airports'
import { stationCode } from './stationCode'
import { isWindow } from './window'

export interface PlaceCandidate { code: string; name: string | null; city: string | null; country: string | null }
export interface PlaceValue {
  text: string
  code: string | null
  name: string | null
  city: string | null
  country: string | null
  resolved: boolean
  candidates: PlaceCandidate[]
  /** SA-1052: the customer's alternatives — present only in that state. */
  alternatives?: PlaceCandidate[]
}
export type PlaceState = 'resolved' | 'choice' | 'alternatives' | 'unknown' | 'text'
/** What `Station` needs to show a chip: the two server-worded strings. */
export interface StationWords { displayName: string; placeWords: string }

export const OR = ' or '
export const ALTERNATIVES_MAX = 5
const collapse = (s: string): string => s.split(/\s+/).filter(Boolean).join(' ')

export function isPlace(v: unknown): v is PlaceValue {
  // SA-1136: a window is also an object with a `text`; it is never a place.
  return !!v && typeof v === 'object' && !Array.isArray(v) && typeof (v as { text?: unknown }).text === 'string' && !isWindow(v)
}

export function placeCode(v: unknown): string | null {
  if (isPlace(v)) return typeof v.code === 'string' && v.code ? v.code : null
  return typeof v === 'string' ? stationCode(v) : null
}

function entries(list: unknown): PlaceCandidate[] {
  const out: PlaceCandidate[] = []
  for (const c of Array.isArray(list) ? list : []) {
    const code = c && typeof c === 'object' ? (c as { code?: unknown }).code : null
    if (typeof code === 'string' && code && !out.some((o) => o.code === code)) out.push(c as PlaceCandidate)
  }
  return out
}

/** SA-1052: the customer's alternatives' codes; [] for any other value, a stored string included. */
export function placeAlternatives(v: unknown): string[] {
  if (!isPlace(v) || placeCode(v)) return []
  return entries(v.alternatives).map((c) => c.code)
}

export function placeCodes(v: unknown): string[] {
  const code = placeCode(v)
  if (code) return [code]
  if (!isPlace(v)) return []
  const alternatives = placeAlternatives(v)
  return alternatives.length ? alternatives : entries(v.candidates).map((c) => c.code)
}

export function placeWords(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (isPlace(v)) {
    const code = placeCode(v)
    if (code) return code
    const codes = placeCodes(v)
    return codes.length ? codes.join(OR) : collapse(v.text)
  }
  return collapse(String(v))
}

export function placeState(v: PlaceValue): PlaceState {
  if (v.resolved && v.code) return 'resolved'
  if (v.code) return 'unknown'
  if (placeAlternatives(v).length) return 'alternatives'
  return placeCodes(v).length ? 'choice' : 'text'
}

/** Equality for a save diff: a changed code, choice or set of alternatives is a change; a legacy string and a typed place never compare equal. */
export function placeKey(v: unknown): string {
  if (isPlace(v)) return `place|${placeState(v)}|${v.text}|${v.code ?? ''}|${placeCodes(v).join(',')}`
  return v === null || v === undefined || v === '' ? '' : String(v)
}

/** The entries of the customer's alternatives, one per code (SA-1052). */
export function alternativeEntries(v: PlaceValue): PlaceCandidate[] {
  return entries(v.alternatives)
}

/** A candidate's or an alternative's stored words, shaped for `Station`; null without a name. */
export function entryStationWords(c: PlaceCandidate): StationWords | null {
  return c.name ? { displayName: c.name, placeWords: [c.city, countryName(c.country)].filter(Boolean).join(', ') } : null
}

const asAlternatives = (list: PlaceCandidate[]): PlaceValue => ({
  text: list.map((c) => c.code).join(OR), code: null, name: null, city: null, country: null,
  resolved: false, candidates: [], alternatives: list,
})

/** A person adds an alternative (SA-1052 spec §5.2) to a single station or to the alternatives already
 * there; the text becomes the codes, the inquiry's own words staying in the intake's sources. */
export function withAlternative(v: PlaceValue, a: Airport): PlaceValue {
  const state = placeState(v)
  const base = state === 'alternatives' ? alternativeEntries(v)
    : v.code ? [{ code: v.code, name: v.name, city: v.city, country: v.country }] : []
  const code = a.iata ?? ''
  if (base.length === 0) return v   // a choice or text never becomes a one-entry list
  if (!code || base.some((c) => c.code === code) || base.length >= ALTERNATIVES_MAX) return v
  return asAlternatives([...base, { code, name: a.name, city: a.city, country: a.country }])
}

/** A person removes one; one left is a single place again (resolved when it carries words). */
export function withoutAlternative(v: PlaceValue, code: string): PlaceValue {
  const rest = alternativeEntries(v).filter((c) => c.code !== code)
  if (rest.length >= 2) return asAlternatives(rest)
  const [c] = rest
  if (!c) return v
  return { text: c.code, code: c.code, name: c.name, city: c.city, country: c.country, resolved: Boolean(c.name), candidates: [] }
}

const unresolved = (text: string, code: string | null): PlaceValue => ({ text, code, name: null, city: null, country: null, resolved: false, candidates: [] })

/** What typing writes: the server resolves at save, so a typed code needs no click. */
export function typedPlace(text: string): PlaceValue | null {
  const trimmed = text.trim()
  return trimmed ? unresolved(trimmed, stationCode(trimmed)) : null
}

export function chosenPlace(text: string, a: Airport): PlaceValue {
  return { text: text.trim() || a.iata || a.name, code: a.iata ?? '', name: a.name, city: a.city, country: a.country, resolved: true, candidates: [] }
}

export function candidateChosen(v: PlaceValue, c: PlaceCandidate): PlaceValue {
  return { text: v.text, code: c.code, name: c.name, city: c.city, country: c.country, resolved: true, candidates: [] }
}

/** The country's name from its ISO-2 code, in the browser; the code itself when the browser cannot name it. */
export function countryName(code: string | null): string | null {
  if (!code) return null
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) ?? code
  } catch {
    return code
  }
}

/** The stored words of a resolved place, shaped for `Station` when no lookup row is at hand. */
export function placeStationWords(v: PlaceValue): StationWords | null {
  if (!v.resolved || !v.code || !v.name) return null
  return { displayName: v.name, placeWords: [v.city, countryName(v.country)].filter(Boolean).join(', ') }
}
